import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Share2, Bookmark, ArrowLeft, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getDossiers, getDossierById, getLatestDossiers } from '../services/api';
import ArticleChat from '../components/ArticleChat';
import ShareButtons from '../components/ShareButtons';
import { formatDateEST, formatDateESTShort } from '../utils/dateUtils';

const SERIF_FONT = "'Libre Baskerville', Georgia, serif";

// Strip HTML tags from text (for summaries that may have <p> tags)
const stripHtml = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800',
  'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800',
  'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800',
  'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800',
];

const getImageUrl = (item, index = 0) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
};

// ============================================
// RELATED NEWS CARD - Square Mosaic Style
// ============================================
const RelatedNewsCard = ({ article, index }) => {
  const imageUrl = getImageUrl(article, index);
  
  return (
    <Link 
      to={`/dossiers/${article.dossier_id}`}
      className="group block"
      data-testid={`related-news-${article.dossier_id}`}
    >
      <div className="aspect-square mb-3 overflow-hidden rounded-lg bg-gray-100">
        <img 
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>
      <h3 
        className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug line-clamp-2 mb-1"
        style={{ fontFamily: SERIF_FONT }}
      >
        {article.title}
      </h3>
      <p className="text-xs text-gray-500">
        Por {article.author || 'Editorial'}
      </p>
    </Link>
  );
};

// ============================================
// DOSSIERS LIST PAGE - Light Theme
// ============================================
export const DossiersListPage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getDossiers({ status: 'active' });
        console.log('✅ Dossiers cargados:', data?.length || 0);
        
        if (Array.isArray(data) && data.length > 0) {
          setDossiers(data);
        } else {
          console.warn('⚠️ No se encontraron dossiers activos');
          setDossiers([]);
        }
      } catch (error) {
        console.error('❌ Error cargando dossiers:', error);
        setDossiers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filters = ['all', 'inteligencia-artificial', 'software', 'hardware', 'startups', 'ciberseguridad', 'gaming', 'actualidad'];
  
  const filteredDossiers = filter === 'all' 
    ? dossiers 
    : dossiers.filter(d => {
        const sector = d.sector?.toLowerCase().trim() || '';
        return sector === filter || sector === filter.replace('í', 'i');
      });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 
            className="text-4xl font-bold text-gray-900" 
            style={{ fontFamily: SERIF_FONT }}
          >
            Noticias
          </h1>
          <p className="text-gray-500 mt-2">Las últimas noticias de tecnología e innovación</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-200 bg-white sticky top-[64px] z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-6 py-4 overflow-x-auto scrollbar-hide">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-sm font-medium whitespace-nowrap pb-2 border-b-2 transition-colors ${
                  filter === f
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {f === 'all' ? 'Todas' : 
                 f === 'inteligencia-artificial' ? 'IA' :
                 f === 'software' ? 'Software' :
                 f === 'hardware' ? 'Hardware' :
                 f === 'startups' ? 'Startups' :
                 f === 'ciberseguridad' ? 'Ciberseguridad' :
                 f === 'gaming' ? 'Gaming' :
                 f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Article */}
        {filteredDossiers[0] && (
          <Link to={`/dossiers/${filteredDossiers[0].dossier_id}`} className="block group mb-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-[16/10] overflow-hidden rounded-xl bg-gray-100 shadow-sm">
                <img
                  src={getImageUrl(filteredDossiers[0], 0)}
                  alt={filteredDossiers[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {filteredDossiers[0].sector || 'DESTACADO'}
                  {filteredDossiers[0].primary_country && filteredDossiers[0].primary_country !== 'Global' && (
                    <span className="ml-2 text-blue-600">· {filteredDossiers[0].primary_country}</span>
                  )}
                </p>
                <h2
                  className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-4"
                  style={{ fontFamily: SERIF_FONT }}
                >
                  {filteredDossiers[0].title}
                </h2>
                <p className="text-gray-600 line-clamp-3 mb-4">
                  {filteredDossiers[0].what_changed_today || filteredDossiers[0].executive_summary?.[0]}
                </p>
                <p className="text-sm text-gray-500">
                  Por <span className="text-gray-700 font-medium">{filteredDossiers[0].author || 'Equipo Editorial'}</span>
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredDossiers.slice(1).map((dossier, i) => (
            <Link
              key={dossier.dossier_id}
              to={`/dossiers/${dossier.dossier_id}`}
              className="block group"
            >
              <div className="aspect-[16/10] overflow-hidden rounded-xl bg-gray-100 mb-4 shadow-sm">
                <img
                  src={getImageUrl(dossier, i + 1)}
                  alt={dossier.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                {dossier.sector}
                {dossier.primary_country && dossier.primary_country !== 'Global' && (
                  <span className="ml-2 text-blue-600">· {dossier.primary_country}</span>
                )}
              </p>
              <h3
                className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2"
                style={{ fontFamily: SERIF_FONT }}
              >
                {dossier.title}
              </h3>
              <p className="text-sm text-gray-500">
                Por {dossier.author || 'Equipo Editorial'}
              </p>
            </Link>
          ))}
        </div>

        {filteredDossiers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay noticias en esta categoría
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DOSSIER DETAIL PAGE - Light Theme
// ============================================
export const DossierDetailPage = () => {
  const { dossierId } = useParams();
  const [dossier, setDossier] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const [data, latestData] = await Promise.all([
          getDossierById(dossierId),
          getLatestDossiers()
        ]);
        setDossier(data);
        // Filter out current article and get up to 8 related news
        const related = (latestData || [])
          .filter(d => d.dossier_id !== dossierId)
          .slice(0, 8);
        setRelatedNews(related);
        import('../services/api').then(api => api.trackDossierView(dossierId));
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [dossierId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Noticia no encontrada</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="dossier-detail-page">
      {/* Breadcrumb */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link 
            to="/dossiers" 
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Noticias
          </Link>
        </div>
      </div>

      {/* Cover Image */}
      {dossier.cover_image && (
        <div className="w-full bg-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="aspect-[16/9] w-full overflow-hidden flex items-center justify-center">
              <img 
                src={dossier.cover_image?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${dossier.cover_image}` : dossier.cover_image}
                alt={dossier.title}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Category Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider">
            {dossier.sector || dossier.category || 'TECNOLOGÍA'}
          </span>
          {dossier.primary_country && dossier.primary_country !== 'Global' && (
            <span className="ml-2 text-gray-500 text-xs">· {dossier.primary_country}</span>
          )}
        </div>

        {/* Title */}
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6"
          style={{ fontFamily: SERIF_FONT }}
        >
          {dossier.title}
        </h1>

        {/* Summary */}
        {(dossier.summary || dossier.what_changed_today || dossier.executive_summary?.[0]) && (
          <div className="mb-8 p-5 rounded-xl bg-gray-50 border-l-4 border-blue-500">
            <p className="text-lg text-gray-700 leading-relaxed italic">
              {stripHtml(dossier.summary || dossier.what_changed_today || dossier.executive_summary?.[0])}
            </p>
          </div>
        )}

        {/* Author & Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-gray-900 font-semibold">{dossier.author || 'Equipo Editorial'}</p>
              <p className="text-gray-500 text-sm flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatDateEST(dossier.published_at || dossier.created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button 
              variant="outline" 
              size="sm" 
              className="text-gray-600 hover:text-blue-600 hover:border-blue-600 border-gray-300"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Compartir
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-gray-600 hover:text-blue-600 hover:border-blue-600 border-gray-300"
            >
              <Bookmark className="w-4 h-4 mr-1" />
              Guardar
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-800 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-blockquote:border-l-blue-500 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:text-gray-700 prose-blockquote:italic">
          {dossier.content ? (
            <div 
              className="article-content"
              dangerouslySetInnerHTML={{ 
                __html: (() => {
                  let html = dossier.content;
                  // Remove markdown code blocks
                  html = html.replace(/```html|```/g, '');
                  // If content has <p> tags, use as-is (it's HTML)
                  if (html.includes('<p>')) return html;
                  // Otherwise convert plain text to paragraphs
                  return html.split('\n\n').map(p => `<p>${p.trim()}</p>`).join('');
                })()
              }}
            />
          ) : (
            dossier.executive_summary?.map((point, i) => (
              <p key={i} className="text-gray-800 leading-relaxed mb-6 text-lg">{point}</p>
            ))
          )}
        </div>

        {/* Share Buttons */}
        <ShareButtons 
          url={`https://latechnologia.com/dossiers/${dossier.dossier_id}`}
          title={dossier.title}
        />

        {/* Key Points */}
        {dossier.key_points?.length > 0 && (
          <div className="mt-12 p-6 rounded-xl bg-gray-50 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm">✓</span>
              Puntos Clave
            </h3>
            <ul className="space-y-3">
              {dossier.key_points.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="text-blue-600 font-bold mt-0.5">→</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Signals to Watch */}
        {dossier.signals_to_watch?.length > 0 && (
          <div className="mt-8 p-6 rounded-xl bg-gray-50 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-gray-700 text-white flex items-center justify-center text-sm">👁</span>
              Señales a Observar
            </h3>
            <ul className="space-y-2">
              {dossier.signals_to_watch.map((signal, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 mt-1">•</span>
                  {signal}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tags */}
        {(dossier.related_topics?.length > 0 || dossier.category) && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-3">Temas relacionados:</p>
            <div className="flex flex-wrap gap-2">
              {dossier.category && (
                <span className="px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium">
                  {dossier.category}
                </span>
              )}
              {dossier.related_topics?.map((topic, i) => (
                <span 
                  key={i} 
                  className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700 border border-gray-200 hover:border-blue-500 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* ============================================ */}
      {/* RELATED NEWS - Square Mosaic Grid */}
      {/* ============================================ */}
      {relatedNews.length > 0 && (
        <section className="bg-gray-50 border-t border-gray-200 py-10" data-testid="related-news-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                Otras Noticias
              </h2>
              <Link 
                to="/dossiers" 
                className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-1"
              >
                Ver todas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Desktop: 4-Column Square Grid */}
            <div className="hidden md:grid grid-cols-4 gap-6">
              {relatedNews.map((article, i) => (
                <RelatedNewsCard 
                  key={article.dossier_id} 
                  article={article} 
                  index={i}
                />
              ))}
            </div>
            
            {/* Mobile: 2-Column Grid */}
            <div className="grid grid-cols-2 gap-4 md:hidden">
              {relatedNews.slice(0, 4).map((article, i) => (
                <RelatedNewsCard 
                  key={article.dossier_id} 
                  article={article} 
                  index={i}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Article Chat */}
      <ArticleChat 
        dossierId={dossier.dossier_id} 
        articleTitle={dossier.title}
      />
    </div>
  );
};

export default DossiersListPage;
